import { useEffect, useState } from "react";
import api from "../services/api";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Wallet as WalletIcon, CreditCard, Check, Star, Zap, Package, Clock, Shield, Loader2, ChevronRight, Gift, IndianRupee, Lock, X } from "lucide-react";
import { toast } from "sonner";

const TIER_FEATURES = {
  bronze: [
    { icon: Gift, label: "+5% bonus credits on recharge" },
  ],
  silver: [
    { icon: CreditCard, label: "10% discount on base delivery fees" },
    { icon: Package, label: "1 free fragile packaging per month" },
  ],
  gold: [
    { icon: Zap, label: "Flat rate for first 3 km deliveries" },
    { icon: Clock, label: "Priority pickup within 15 minutes" },
  ],
};

const TIER_COLORS = {
  bronze: { bg: "bg-amber-50", border: "border-amber-300", text: "text-amber-800", accent: "#CD7F32", ring: "ring-amber-300" },
  silver: { bg: "bg-slate-50", border: "border-slate-300", text: "text-slate-700", accent: "#9CA3AF", ring: "ring-slate-300" },
  gold: { bg: "bg-yellow-50", border: "border-yellow-400", text: "text-yellow-800", accent: "#F59E0B", ring: "ring-yellow-400" },
};


function PaymentGateway({ open, onClose, tierLabel, amount, tier, onSuccess }) {
  const [step, setStep] = useState("select");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setStep("select");
      setLoading(false);
    }
  }, [open]);

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (document.getElementById("razorpay-script")) return resolve(true);
      const script = document.createElement("script");
      script.id = "razorpay-script";
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handlePay = async () => {
    setLoading(true);
    try {
      const loaded = await loadRazorpayScript();
      if (!loaded) { toast.error("Failed to load Razorpay"); setLoading(false); return; }

      const { data } = await api.post("/wallet/razorpay-order", { amount, tier: tier || null });

      const options = {
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        name: "PickNGo",
        description: `Wallet Recharge — ${tierLabel}`,
        order_id: data.order_id,
        handler: async (response) => {
          setStep("processing");
          try {
            const verifyRes = await api.post("/wallet/razorpay-verify", {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            setStep("success");
            toast.success(`INR ${amount} credited to wallet!`);
            setTimeout(() => onSuccess(), 1500);
          } catch (err) {
            setStep("failed");
            toast.error("Payment verification failed");
          }
        },
        modal: { ondismiss: () => { setLoading(false); } },
        prefill: {},
        theme: { color: "#FF6B00" },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", () => { setStep("failed"); });
      rzp.open();
      setLoading(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to initiate payment");
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && step !== "processing") onClose(); }}>
      <DialogContent className="sm:max-w-md p-0 overflow-hidden rounded-2xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-slate-900 to-slate-800 px-6 py-5 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#FF6B00] rounded-xl flex items-center justify-center shadow-sm">
                <span className="font-['Outfit'] text-sm font-extrabold text-white">P</span>
              </div>
              <div>
                <p className="text-sm font-bold">PickNGo Payments</p>
                <p className="text-[10px] text-slate-400">Wallet Recharge — {tierLabel}</p>
              </div>
            </div>
            <div className="flex items-center gap-1 text-xs text-slate-400">
              <Lock className="h-3 w-3" /> Secure
            </div>
          </div>
          <div className="mt-4 flex items-baseline gap-1">
            <span className="text-3xl font-['Outfit'] font-extrabold tracking-tight">
              <IndianRupee className="h-5 w-5 inline -mt-1" />{amount.toLocaleString()}
            </span>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          {step === "select" && (
            <div className="space-y-4" data-testid="payment-method-select">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Razorpay Secure Payment</p>
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                <p className="text-sm text-slate-600">Supports UPI, Cards, Net Banking, Wallets</p>
                <p className="text-[10px] text-slate-400 mt-1">You'll be redirected to Razorpay's secure checkout</p>
              </div>
              <Button
                onClick={handlePay}
                disabled={loading}
                className="w-full h-12 bg-[#FF6B00] text-white hover:bg-[#E65A00] rounded-xl font-bold text-sm shadow-sm shadow-[#FF6B00]/20"
                data-testid="pay-now-btn"
              >
                {loading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Initializing...</> : `Pay INR ${amount.toLocaleString()}`}
              </Button>
              <p className="text-center text-[10px] text-slate-400 flex items-center justify-center gap-1">
                <Lock className="h-2.5 w-2.5" /> 256-bit SSL encrypted payment via Razorpay
              </p>
            </div>
          )}

          {step === "processing" && (
            <div className="py-10 text-center" data-testid="payment-processing">
              <div className="relative mx-auto w-16 h-16 mb-5">
                <div className="absolute inset-0 border-4 border-slate-100 rounded-full" />
                <div className="absolute inset-0 border-4 border-[#FF6B00] rounded-full border-t-transparent animate-spin" />
                <div className="absolute inset-3 bg-orange-50 rounded-full flex items-center justify-center">
                  <IndianRupee className="h-5 w-5 text-[#FF6B00]" />
                </div>
              </div>
              <p className="text-base font-semibold text-slate-800">Processing Payment...</p>
              <p className="text-sm text-slate-400 mt-1">Please wait, do not close this window</p>
            </div>
          )}

          {step === "success" && (
            <div className="py-10 text-center" data-testid="payment-success">
              <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-5 ring-4 ring-emerald-100">
                <Check className="h-8 w-8 text-emerald-600" />
              </div>
              <p className="text-base font-bold text-emerald-700">Payment Successful!</p>
              <p className="text-sm text-slate-500 mt-1">INR {amount.toLocaleString()} credited to your wallet</p>
            </div>
          )}

          {step === "failed" && (
            <div className="py-10 text-center" data-testid="payment-failed">
              <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-5 ring-4 ring-red-100">
                <X className="h-8 w-8 text-red-600" />
              </div>
              <p className="text-base font-bold text-red-700">Payment Failed</p>
              <p className="text-sm text-slate-500 mt-1">Please try again</p>
              <Button onClick={() => setStep("select")} className="mt-4 bg-slate-800 text-white hover:bg-slate-700 rounded-xl">
                Retry
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function WalletPage() {
  const [wallet, setWallet] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recharging, setRecharging] = useState(null);
  const [coupons, setCoupons] = useState([]);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);
  const [transactions, setTransactions] = useState([]);

  const fetchData = async () => {
    try {
      const [walletRes, tiersRes, couponsRes, txnsRes] = await Promise.all([
        api.get("/wallet/balance"),
        api.get("/wallet/tiers"),
        api.get("/coupons/my-coupons"),
        api.get("/wallet/transactions?limit=25").catch(() => ({ data: { transactions: [] } })),
      ]);
      setWallet(walletRes.data);
      setTiers(tiersRes.data.tiers);
      setCoupons(couponsRes.data.coupons || []);
      setTransactions(txnsRes.data?.transactions || []);
    } catch (err) {
      console.error("Failed to load wallet data:", err);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchData only needs to run on mount; adding it creates an infinite loop since it isn't memoized.
  useEffect(() => { fetchData(); }, []);

  const openPayment = (tier) => {
    const tierData = tiers.find((t) => t.id === tier);
    setSelectedTier({ id: tier, label: tierData?.label || tier, amount: tierData?.recharge_amount || 5000 });
    setPaymentOpen(true);
  };

  const handlePaymentSuccess = async () => {
    if (!selectedTier) return;
    setRecharging(selectedTier.id);
    setPaymentOpen(false);
    try {
      const { data } = await api.post("/wallet/recharge", { tier: selectedTier.id });
      toast.success(`${data.tier_label} activated! Credited: INR ${data.credited.toLocaleString()}`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Recharge failed");
    } finally {
      setRecharging(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-[#FF6B00]" />
      </div>
    );
  }

  const activeTier = wallet?.tier;

  return (
    <div className="space-y-8 max-w-5xl" data-testid="wallet-page">
      {/* Header */}
      <div className="animate-fadeInUp">
        <h1 className="font-['Outfit'] text-2xl sm:text-3xl font-bold text-slate-900" data-testid="wallet-heading">
          B2B Wallet
        </h1>
        <p className="text-sm text-slate-500 mt-1">Prepaid wallet for business deliveries — unlock discounts and priority perks</p>
      </div>

      {/* Balance Card */}
      <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl p-6 sm:p-8 text-white shadow-xl animate-fadeInUp stagger-1" data-testid="wallet-balance-card">
        <div className="flex items-start justify-between mb-6">
          <div>
            <p className="text-sm text-slate-400 font-medium uppercase tracking-wider">Wallet Balance</p>
            <p className="text-4xl sm:text-5xl font-bold mt-2 font-['Outfit'] tracking-tight" data-testid="wallet-balance-amount">
              INR {(wallet?.balance || 0).toLocaleString()}
            </p>
          </div>
          <div className="w-14 h-14 bg-white/10 rounded-2xl flex items-center justify-center backdrop-blur-sm">
            <WalletIcon className="h-7 w-7 text-white/80" />
          </div>
        </div>
        {activeTier && (
          <div className="flex items-center gap-3">
            <span
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider"
              style={{ backgroundColor: `${TIER_COLORS[activeTier]?.accent}20`, color: TIER_COLORS[activeTier]?.accent }}
              data-testid="wallet-active-tier"
            >
              <Shield className="h-3.5 w-3.5" />
              {wallet.tier_label}
            </span>
            {wallet.tier_benefits?.priority_pickup && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-bold bg-green-500/20 text-green-400">
                <Zap className="h-3 w-3" /> Priority Pickup
              </span>
            )}
            {wallet.tier_benefits?.discount_on_base_pct > 0 && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-bold bg-blue-500/20 text-blue-400">
                {wallet.tier_benefits.discount_on_base_pct}% off deliveries
              </span>
            )}
          </div>
        )}
      </div>

      {/* Tier Plans */}
      <div className="animate-fadeInUp stagger-2">
        <h2 className="text-base sm:text-lg font-semibold text-slate-900 mb-4">Choose a Plan</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="wallet-tiers-grid">
          {tiers.map((tier) => {
            const colors = TIER_COLORS[tier.id] || TIER_COLORS.bronze;
            const features = TIER_FEATURES[tier.id] || [];
            const isActive = activeTier === tier.id;
            return (
              <div
                key={tier.id}
                className={`relative rounded-2xl border-2 p-5 transition-all ${
                  isActive ? `${colors.border} ${colors.bg} ring-2 ${colors.ring}` : "border-slate-200 bg-white hover:border-slate-300"
                }`}
                data-testid={`wallet-tier-${tier.id}`}
              >
                {isActive && (
                  <span className="absolute -top-2.5 right-4 bg-[#FF6B00] text-white text-[10px] font-bold px-2.5 py-0.5 rounded-full uppercase tracking-wider">
                    Active
                  </span>
                )}
                <div className="flex items-center gap-2 mb-3">
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: `${tier.color}20` }}
                  >
                    <Star className="h-4 w-4" style={{ color: tier.color }} />
                  </div>
                  <span className={`text-sm font-bold ${colors.text}`}>{tier.label}</span>
                </div>
                <p className="text-2xl font-bold text-slate-900 mb-3 font-['Outfit']">
                  INR {tier.recharge_amount.toLocaleString()}
                </p>
                <ul className="space-y-2 mb-4">
                  {features.map((f, i) => (
                    <li key={f || i} className="flex items-center gap-2 text-xs text-slate-600">
                      <f.icon className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                      {f.label}
                    </li>
                  ))}
                </ul>
                <Button
                  onClick={() => openPayment(tier.id)}
                  disabled={recharging === tier.id}
                  className={`w-full rounded-xl h-10 text-sm font-semibold btn-press ${
                    isActive
                      ? "bg-slate-200 text-slate-600 hover:bg-slate-300"
                      : "bg-[#FF6B00] text-white hover:bg-[#E65A00]"
                  }`}
                  data-testid={`wallet-recharge-${tier.id}`}
                >
                  {recharging === tier.id ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                  ) : isActive ? (
                    <Check className="h-4 w-4 mr-1.5" />
                  ) : (
                    <ChevronRight className="h-4 w-4 mr-1.5" />
                  )}
                  {isActive ? "Top Up Again" : "Recharge"}
                </Button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Coupons */}
      {coupons.length > 0 && (
        <div className="animate-fadeInUp stagger-3" data-testid="wallet-coupons">
          <h2 className="text-base sm:text-lg font-semibold text-slate-900 mb-4">Your Coupons</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {coupons.map((c, i) => (
              <div key={c.code || i} className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-xl p-4" data-testid={`coupon-${c.code}`}>
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center shrink-0">
                  <Gift className="h-5 w-5 text-emerald-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-emerald-800 font-mono">{c.code}</p>
                  <p className="text-xs text-emerald-600">{c.description || `${c.discount_pct}% off (max INR ${c.max_discount})`}</p>
                </div>
                {c.used ? (
                  <span className="text-[10px] font-bold text-slate-400 bg-slate-100 px-2 py-0.5 rounded">USED</span>
                ) : (
                  <span className="text-[10px] font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded">ACTIVE</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Activity — unified ledger (refunds, pay-now debits, recharges, etc.) */}
      {transactions.length > 0 && (
        <div className="animate-fadeInUp stagger-4" data-testid="wallet-transactions">
          <h2 className="text-base sm:text-lg font-semibold text-slate-900 mb-4">Recent Activity</h2>
          <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
            {transactions.map((t, i) => {
              const isCredit = t.type === "credit";
              return (
                <div
                  key={t.id || `${t.created_at}-${i}`}
                  className={`flex items-center justify-between px-5 py-3.5 ${i > 0 ? "border-t border-slate-50" : ""}`}
                  data-testid={`wallet-txn-${t.source || "row"}-${i}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                      isCredit ? "bg-emerald-50 text-emerald-600" : "bg-rose-50 text-rose-600"
                    }`}>
                      {isCredit ? <Gift className="h-4 w-4" /> : <CreditCard className="h-4 w-4" />}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-800 truncate" title={t.description}>
                        {t.category || (isCredit ? "Credit" : "Debit")}
                        {t.tracking_id && (
                          <span className="ml-2 text-[10px] font-mono font-medium text-slate-400">{t.tracking_id}</span>
                        )}
                      </p>
                      <p className="text-[10px] text-slate-400 truncate" title={t.description}>
                        {t.description || "—"}
                      </p>
                    </div>
                  </div>
                  <div className="text-right shrink-0 pl-3">
                    <p className={`text-sm font-bold ${isCredit ? "text-emerald-600" : "text-rose-600"}`}>
                      {isCredit ? "+" : "−"}INR {Number(t.amount || 0).toLocaleString()}
                    </p>
                    {t.created_at && (
                      <p className="text-[10px] text-slate-400">
                        {new Date(t.created_at).toLocaleString()}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Recharge History */}
      {wallet?.recharge_history?.length > 0 && (
        <div className="animate-fadeInUp stagger-4" data-testid="wallet-history">
          <h2 className="text-base sm:text-lg font-semibold text-slate-900 mb-4">Recharge History</h2>
          <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
            {wallet.recharge_history.slice().reverse().map((entry, i) => (
              <div key={`${entry.date}-${entry.tier}-${i}`} className={`flex items-center justify-between px-5 py-3.5 ${i > 0 ? "border-t border-slate-50" : ""}`}>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${TIER_COLORS[entry.tier]?.accent}15` }}>
                    <Star className="h-4 w-4" style={{ color: TIER_COLORS[entry.tier]?.accent }} />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-800 capitalize">{entry.tier} Plan</p>
                    <p className="text-[10px] text-slate-400">{new Date(entry.date).toLocaleDateString()}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold text-slate-900">+INR {entry.credited.toLocaleString()}</p>
                  {entry.bonus > 0 && <p className="text-[10px] text-emerald-600 font-medium">incl. INR {entry.bonus} bonus</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Payment Gateway Modal */}
      {selectedTier && (
        <PaymentGateway
          open={paymentOpen}
          onClose={() => setPaymentOpen(false)}
          tierLabel={selectedTier.label}
          amount={selectedTier.amount}
          tier={selectedTier.id}
          onSuccess={handlePaymentSuccess}
        />
      )}
    </div>
  );
}
